import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export class ZipCodeValidator {
  /**
   * USA ZIP code formats supported:
   * - 12345 (5-digit)
   * - 12345-6789 (ZIP+4)
   * - 12345 6789 (ZIP+4 with space)
   */
  static readonly USA_ZIP_PATTERN = /^[0-9]{5}(?:[-\s]?[0-9]{4})?$/;

  /**
   * Strict USA ZIP code pattern (5 digits only)
   * Format: 12345
   */
  static readonly USA_ZIP_5_PATTERN = /^[0-9]{5}$/;

  /**
   * ZIP+4 format pattern
   * Formats: 12345-6789, 12345 6789
   */
  static readonly USA_ZIP_PLUS4_PATTERN = /^[0-9]{5}[-\s][0-9]{4}$/;

  /**
   * Invalid ZIP code prefixes (commonly known invalid ranges)
   * These are ZIP codes that are not assigned or are reserved
   */
  static readonly INVALID_ZIP_PREFIXES = [
    '00', // Not assigned
    '96', '97', '98', '99' // Reserved for military and other special uses (partially)
  ];

  /**
   * Validator function for USA ZIP codes (supports both 5-digit and ZIP+4)
   * @param control - The form control to validate
   * @returns ValidationErrors | null
   */
  static usaZipCode(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const zipCode = control.value.toString().trim();
    
    // Check if it matches USA ZIP code pattern
    if (!ZipCodeValidator.USA_ZIP_PATTERN.test(zipCode)) {
      return { 
        usaZipCode: { 
          value: zipCode,
          message: 'Please enter a valid  ZIP code '
        } 
      };
    }

    // Extract the 5-digit ZIP code part
    const zipDigits = zipCode.substring(0, 5);
    
    // Check for invalid ZIP code prefixes
    const prefix = zipDigits.substring(0, 2);
    if (ZipCodeValidator.INVALID_ZIP_PREFIXES.includes(prefix)) {
      return { 
        usaZipCode: { 
          value: zipCode,
          message: 'Please enter a valid  ZIP code'
        } 
      };
    }

    // Validate ZIP+4 format if present
    if (zipCode.length > 5) {
      if (!ZipCodeValidator.USA_ZIP_PLUS4_PATTERN.test(zipCode)) {
        return { 
          usaZipCode: { 
            value: zipCode,
            message: 'ZIP+4 format should be 12345-6789 or 12345 6789'
          } 
        };
      }
    }

    return null; // Valid ZIP code
  }

  /**
   * Validator function for strict 5-digit USA ZIP codes only
   * @param control - The form control to validate
   * @returns ValidationErrors | null
   */
  static usaZipCode5(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const zipCode = control.value.toString().trim();
    
    if (!ZipCodeValidator.USA_ZIP_5_PATTERN.test(zipCode)) {
      return { 
        usaZipCode5: { 
          value: zipCode,
          message: 'Please enter a valid 5-digit  ZIP code '
        } 
      };
    }

    // Check for invalid ZIP code prefixes
    const prefix = zipCode.substring(0, 2);
    if (ZipCodeValidator.INVALID_ZIP_PREFIXES.includes(prefix)) {
      return { 
        usaZipCode5: { 
          value: zipCode,
          message: 'Please enter a valid  ZIP code'
        } 
      };
    }

    return null; // Valid ZIP code
  }

  /**
   * Validator function for ZIP+4 format only
   * @param control - The form control to validate
   * @returns ValidationErrors | null
   */
  static usaZipCodePlus4(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const zipCode = control.value.toString().trim();
    
    if (!ZipCodeValidator.USA_ZIP_PLUS4_PATTERN.test(zipCode)) {
      return { 
        usaZipCodePlus4: { 
          value: zipCode,
          message: 'Please enter a valid ZIP+4 code (e.g., 12345-6789)'
        } 
      };
    }

    return ZipCodeValidator.usaZipCode(control);
  }

  /**
   * Format ZIP code to standard USA format
   * @param zipCode - Raw ZIP code string
   * @param forceZipPlus4 - Whether to enforce ZIP+4 format
   * @returns Formatted ZIP code or original if invalid
   */
  static formatUsaZipCode(zipCode: string, forceZipPlus4 = false): string {
    if (!zipCode) return zipCode;

    const digitsOnly = zipCode.replace(/\D/g, '');
    
    if (digitsOnly.length === 5) {
      return forceZipPlus4 ? `${digitsOnly}-0000` : digitsOnly;
    } else if (digitsOnly.length === 9) {
      return `${digitsOnly.substring(0, 5)}-${digitsOnly.substring(5)}`;
    }

    return zipCode; // Return original if can't format
  }

  /**
   * Remove formatting from ZIP code (digits only)
   * @param zipCode - Formatted ZIP code
   * @returns Digits only string
   */
  static stripFormatting(zipCode: string): string {
    if (!zipCode) return zipCode;
    return zipCode.replace(/\D/g, '');
  }

  /**
   * Get ZIP code type (5-digit or ZIP+4)
   * @param zipCode - ZIP code to analyze
   * @returns 'zip5' | 'zip+4' | 'invalid'
   */
  static getZipCodeType(zipCode: string): 'zip5' | 'zip+4' | 'invalid' {
    if (!zipCode) return 'invalid';
    
    const cleaned = zipCode.replace(/[-\s]/g, '');
    
    if (/^[0-9]{5}$/.test(cleaned)) {
      return 'zip5';
    } else if (/^[0-9]{9}$/.test(cleaned)) {
      return 'zip+4';
    }
    
    return 'invalid';
  }

  /**
   * Check if ZIP code is valid USA format
   * @param zipCode - ZIP code to validate
   * @returns boolean
   */
  static isValidUsaZipCode(zipCode: string): boolean {
    if (!zipCode) return false;
    
    const control = { value: zipCode } as AbstractControl;
    return ZipCodeValidator.usaZipCode(control) === null;
  }

  /**
   * Get state/region from ZIP code (basic mapping for common ranges)
   * This is a simplified mapping - for production use, consider a more comprehensive database
   * @param zipCode - 5-digit ZIP code
   * @returns Estimated state abbreviation or null
   */
  static getStateFromZipCode(zipCode: string): string | null {
    if (!zipCode) return null;
    
    const zip5 = zipCode.substring(0, 5);
    const zipNum = parseInt(zip5, 10);
    
    // Basic ZIP code to state mapping (simplified)
    const zipRanges: Record<string, [number, number]> = {
      'MA': [1000, 2799], // Massachusetts
      'RI': [2800, 2999], // Rhode Island  
      'NH': [3000, 3899], // New Hampshire
      'ME': [3900, 4999], // Maine
      'VT': [5000, 5999], // Vermont
      'CT': [6000, 6999], // Connecticut
      'NJ': [7000, 8999], // New Jersey
      'NY': [10000, 14999], // New York
      'PA': [15000, 19699], // Pennsylvania
      'DE': [19700, 19999], // Delaware
      'DC': [20000, 20599], // Washington DC
      'MD': [20600, 21999], // Maryland
      'VA': [22000, 24699], // Virginia
      'WV': [24700, 26999], // West Virginia
      'NC': [27000, 28999], // North Carolina
      'SC': [29000, 29999], // South Carolina
      'GA': [30000, 31999], // Georgia
      'FL': [32000, 34999], // Florida
      'AL': [35000, 36999], // Alabama
      'TN': [37000, 38599], // Tennessee
      'MS': [38600, 39999], // Mississippi
      'KY': [40000, 42799], // Kentucky
      'OH': [43000, 45999], // Ohio
      'IN': [46000, 47999], // Indiana
      'MI': [48000, 49999], // Michigan
      'IA': [50000, 52899], // Iowa
      'MN': [55000, 56799], // Minnesota
      'SD': [57000, 57799], // South Dakota
      'ND': [58000, 58899], // North Dakota
      'MT': [59000, 59999], // Montana
      'IL': [60000, 62999], // Illinois
      'MO': [63000, 65899], // Missouri
      'KS': [66000, 67999], // Kansas
      'NE': [68000, 69399], // Nebraska
      'LA': [70000, 71499], // Louisiana
      'AR': [71600, 72999], // Arkansas
      'OK': [73000, 74999], // Oklahoma
      'TX': [75000, 79999], // Texas
      'CO': [80000, 81699], // Colorado
      'WY': [82000, 83199], // Wyoming
      'UT': [84000, 84799], // Utah
      'AZ': [85000, 86599], // Arizona
      'NM': [87000, 88499], // New Mexico
      'NV': [89000, 89899], // Nevada
      'CA': [90000, 96199], // California
      'HI': [96700, 96899], // Hawaii
      'AK': [99500, 99999], // Alaska
    };

    for (const [state, [min, max]] of Object.entries(zipRanges)) {
      if (zipNum >= min && zipNum <= max) {
        return state;
      }
    }
    
    return null; // Unknown or invalid range
  }
}

/**
 * Factory function to create USA ZIP code validator (supports 5-digit and ZIP+4)
 * @returns ValidatorFn
 */
export function usaZipCodeValidator(): ValidatorFn {
  return ZipCodeValidator.usaZipCode;
}

/**
 * Factory function to create 5-digit USA ZIP code validator
 * @returns ValidatorFn
 */
export function usaZipCode5Validator(): ValidatorFn {
  return ZipCodeValidator.usaZipCode5;
}

/**
 * Factory function to create ZIP+4 validator
 * @returns ValidatorFn
 */
export function usaZipCodePlus4Validator(): ValidatorFn {
  return ZipCodeValidator.usaZipCodePlus4;
} 